'use client';

import { isNumber } from 'lodash';
import type {
  InvoiceLineItem,
  PlanPeriod,
  PlanQuote,
} from '@frameio/graphql-client/generated';
import { date } from '@frameio/i18n';
import { formatBytes } from '@frameio/utils/formatters';
import { formatCents } from '@frameio/utils/formatters/formatters';
import { Separator, Text, theme, Tooltip } from '@frameio/vapor';
import { IconInfo } from '@frameio/vapor-icons';
import type { Modifier } from '../../../billing/utils/constants';
import {
  addOnLabel,
  modifierSortOrderIndices,
  storageModifers,
} from '../../../billing/utils/constants';
import { getIsDueNow } from '../../../plans/utils';
import TEXT from './generated/TEXT/PlanSummaryDetails';
import { useGetBillingCountry, useMaybeHasErrorValidatingVat } from './hooks';
import styles from './PlanSummaryDetails.module.css';
import type { ConversionDialogProps } from './types';
import { getCountryNameFromAbbreviation, getIsTaxExempt } from './utils';

const PAUSE_OFFER_DISCOUNT_PERCENT = 75;

TEXT.DEFINE({
  /** Label shown when free trial ends - includes trial end date */
  afterFreeTrialEnds: (expirationDate: Date): string =>
    `After free trial ends ${date(expirationDate, 'medium')}`,
  /** Table column header for item descriptions */
  description: 'Description',
  /** Table column header for quantity (Note: typo in original) */
  quanity: 'Qty',
  /** Table column header for unit price */
  unitPrice: 'Unit price',
  /** Table column header for total amount */
  amount: 'Amount',
  /** Row label for subtotal before tax */
  subtotal: 'Subtotal',
  /** Discount text - standalone */
  discountApplied: (percent: number): string => `${percent}% off applied`,
  /** Format price display - pass pre-formatted price */
  price: (formattedPrice: string): string => formattedPrice,
  /** Period suffix - monthly */
  pricePeriodMonthly: '/mo.',
  /** Period suffix - yearly */
  pricePeriodYearly: '/yr.',
  /** Member count - singular */
  memberIncludedSingular: '1 Member included',
  /** Member count - plural */
  membersIncludedPlural: (count: number): string => `${count} Members included`,
  /** Add-on title format */
  addOnTitle: (label: string): string => `${label} add-on`,
  /** Storage add-on description - size only */
  storageDescriptionSizeOnly: (size: string): string => `${size} each`,
  /** Storage add-on description - size with discount */
  storageDescriptionSizeWithDiscount: (
    size: string,
    discount: string
  ): string => `${size} each • ${discount}`,
  /** Billing frequency - monthly */
  billedMonthly: 'Billed monthly',
  /** Billing frequency - yearly */
  billedYearly: 'Billed yearly',
  /** Tax row label */
  tax: 'Tax',
  /** Due now label - with asterisk if immediate payment required */
  dueNowWithAsterisk: 'Due now*',
  /** Due now label - without asterisk */
  dueNowRegular: 'Due now',
  /** Plan title */
  planTitle: (title: string): string => `${title} plan`,
  /** Plan description - members only (no discount) */
  planDescriptionMembersOnly: (members: string): string => members,
  /** Plan description - members with discount */
  planDescriptionMembersWithDiscount: (
    members: string,
    discount: string
  ): string => `${members} • ${discount}`,
  /** Plan description - discount only (no members) */
  planDescriptionDiscountOnly: (discount: string): string => discount,
  /** Tax percentage with country - for VAT countries */
  taxPercentVAT: (taxPercent: number, billingCountry: string): string =>
    `VAT • ${billingCountry} (${taxPercent}%)`,
  /** Tax percentage with display name - for non-VAT regions */
  taxPercentOther: (taxPercent: number, displayName: string): string =>
    `${displayName} • (${taxPercent}%)`,
  /** VAT excluded text */
  noTaxChargedVAT: 'VAT • (excluded)',
  /** Sales tax excluded text */
  noTaxChargedSalesTaxExcluded: 'Sales Tax • (excluded)',
  /** Sales tax 0% text */
  noTaxChargedSalesTaxZero: 'Sales Tax • (0%)',
  /** Total row label */
  total: 'Total',
  /** Tooltip explaining tax exclusion */
  toolTip:
    'Ensure your Tax ID is correct in your Payment Details to exclude tax.',
  /** Description wrapped in parentheses for collapsed view */
  descriptionCollapsedFormat: (description: string): string =>
    `(${description})`,
});

type InvoiceLineItemWithModifier = InvoiceLineItem & {
  item: { modifier: string };
};

/** @todo (MON-757): this will be consolidated for billing. */
const getUpdatedAddOns = ({ addOns }: { addOns: Array<InvoiceLineItem> }) => {
  if (!addOns) return [];

  const updatedAddons = JSON.parse(JSON.stringify(addOns));

  /** __typename Plan does not include a modifer. This simply sets that. */
  const plan = updatedAddons.find((addOn: InvoiceLineItem) => {
    return addOn.item?.__typename === 'Plan';
  });

  if (plan) {
    plan.item.modifier = 'plan';
  }

  updatedAddons.sort(
    (a: InvoiceLineItemWithModifier, b: InvoiceLineItemWithModifier) => {
      const aIndex = modifierSortOrderIndices[a.item.modifier];
      const bIndex = modifierSortOrderIndices[b.item.modifier];
      return aIndex - bIndex;
    }
  );

  return updatedAddons;
};

const PriceDisplay = ({
  highlightPrice = false,
  isCollapsed,
  amount,
  period,
}: {
  isCollapsed: boolean;
  amount: number;
  period: PlanPeriod;
  highlightPrice?: boolean;
}) => {
  const priceColor = highlightPrice ? 'primary' : 'tertiary';
  const formattedPrice = formatCents(amount, true);
  const pricePeriod =
    period === 'MONTHLY' ? TEXT.pricePeriodMonthly : TEXT.pricePeriodYearly;

  return (
    <div className={styles.priceContainer}>
      <Text
        color={priceColor}
        fontWeight={isCollapsed ? 'regular' : 'semiBold'}
        variant={isCollapsed ? 'secondary' : 'body'}
      >
        {TEXT.price(formattedPrice)}
      </Text>{' '}
      <Text color="tertiary" variant="caption">
        {pricePeriod}
      </Text>
    </div>
  );
};

const getNoTaxChargedText = (
  isTaxExempt: boolean,
  countryNameFromAbbreviation?: string
): string => {
  if (countryNameFromAbbreviation) {
    return TEXT.noTaxChargedVAT;
  }
  return isTaxExempt
    ? TEXT.noTaxChargedSalesTaxExcluded
    : TEXT.noTaxChargedSalesTaxZero;
};

export const PlanSummaryDetails = ({
  planQuote,
  shouldUpgradeSubscriptionImmediately,
  title,
  isCollapsed = false,
  userLimit,
  period,
  isPausedPlan,
  isTrialActive,
  nextBillAt,
  promotionExpiresAt,
  showDueNow = true,
  isCurrentUserSubscriptionFree,
}: {
  planQuote: PlanQuote | null | undefined;
  isCollapsed?: boolean;
  isTrialActive: boolean;
  nextBillAt: string | null | undefined;
  promotionExpiresAt: string | null | undefined;
  showDueNow?: boolean;
  isCurrentUserSubscriptionFree: boolean;
} & Pick<
  ConversionDialogProps,
  | 'period'
  | 'shouldUpgradeSubscriptionImmediately'
  | 'title'
  | 'userLimit'
  | 'isPausedPlan'
>) => {
  const subtotal = planQuote?.subtotal;
  const tax = planQuote?.tax ?? 0;
  const taxLineItems = planQuote?.taxLineItems;
  const total = planQuote?.total;
  const invoiceLineItems = (planQuote?.invoiceLineItems ?? []).filter(
    (item): item is InvoiceLineItem => item !== null
  );
  const noTaxCharged = taxLineItems?.length === 0 || tax === 0;

  const taxExempt = planQuote?.taxExempt;
  const isTaxExempt = taxExempt ? getIsTaxExempt(taxExempt) : false;

  const discountPercent = isPausedPlan
    ? PAUSE_OFFER_DISCOUNT_PERCENT
    : undefined;

  const addOns = getUpdatedAddOns({
    addOns: invoiceLineItems,
  });

  const isDueNow = getIsDueNow(
    !!shouldUpgradeSubscriptionImmediately,
    nextBillAt
  );

  /** If !isDueNow we will show 0 */
  const dueNow = !isDueNow ? 0 : planQuote?.dueNow;

  const showTrialTotal = isTrialActive && !isDueNow;

  const billingCountry = useGetBillingCountry();

  const countryNameFromAbbreviation = billingCountry
    ? getCountryNameFromAbbreviation(billingCountry)
    : '';

  const maybeHasErrorValidatingVat = useMaybeHasErrorValidatingVat(tax);

  const shouldShowMaybeHasErrorValidatingVat =
    maybeHasErrorValidatingVat && !isCollapsed;

  return (
    <>
      <table className={styles.table} data-collapsed={isCollapsed}>
        <thead>
          <tr>
            <th>
              <Text color="tertiary" variant="caption">
                {TEXT.description}
              </Text>
            </th>
            <th>
              <Text color="tertiary" variant="caption">
                {TEXT.quanity}
              </Text>
            </th>
            <th>
              <Text color="tertiary" variant="caption">
                {TEXT.unitPrice}
              </Text>
            </th>
            <th>
              <Text color="tertiary" variant="caption">
                {TEXT.amount}
              </Text>
            </th>
          </tr>
        </thead>

        <tbody>
          {/** add-on */}
          {addOns.map(
            ({
              amount,
              unitAmount: addOnCost,
              item,
              quantity,
            }: InvoiceLineItemWithModifier) => {
              if (quantity === 0) return null;
              const { increment, modifier } = item as {
                increment: number;
                modifier: string;
              };

              // Build description based on modifier type
              let descriptionTitle: string;
              let descriptionText: string;

              if (modifier === 'plan') {
                // Plan description
                descriptionTitle = TEXT.planTitle(title);

                if (isNumber(userLimit)) {
                  const members =
                    userLimit === 1
                      ? TEXT.memberIncludedSingular
                      : TEXT.membersIncludedPlural(userLimit);

                  if (discountPercent) {
                    const discount = TEXT.discountApplied(discountPercent);
                    descriptionText = TEXT.planDescriptionMembersWithDiscount(
                      members,
                      discount
                    );
                  } else {
                    descriptionText = TEXT.planDescriptionMembersOnly(members);
                  }
                } else if (discountPercent) {
                  const discount = TEXT.discountApplied(discountPercent);
                  descriptionText = TEXT.planDescriptionDiscountOnly(discount);
                } else {
                  descriptionText = '';
                }
              } else if (storageModifers.has(modifier as Modifier)) {
                // Storage add-on
                descriptionTitle = TEXT.addOnTitle(addOnLabel[modifier]);
                const size = formatBytes(increment ?? 0);

                if (discountPercent) {
                  const discount = TEXT.discountApplied(discountPercent);
                  descriptionText = TEXT.storageDescriptionSizeWithDiscount(
                    size,
                    discount
                  );
                } else {
                  descriptionText = TEXT.storageDescriptionSizeOnly(size);
                }
              } else {
                // Non-storage add-on
                descriptionTitle = TEXT.addOnTitle(addOnLabel[modifier]);
                descriptionText = discountPercent
                  ? TEXT.discountApplied(discountPercent)
                  : '';
              }

              return (
                <tr key={modifier}>
                  <td>
                    <div
                      className={styles.addOnContainer}
                      data-collapsed={isCollapsed}
                    >
                      <Text
                        color="primary"
                        fontWeight={isCollapsed ? 'regular' : 'medium'}
                        variant={isCollapsed ? 'secondary' : 'primary'}
                      >
                        {descriptionTitle}
                      </Text>
                      {!!descriptionText && (
                        <Text
                          className={styles.planSummaryDescriptionDetails}
                          color={isCollapsed ? 'primary' : 'tertiary'}
                          variant={'secondary'}
                        >
                          {isCollapsed
                            ? TEXT.descriptionCollapsedFormat(descriptionText)
                            : descriptionText}
                        </Text>
                      )}
                    </div>
                  </td>
                  <td>
                    <Text
                      color="tertiary"
                      variant={isCollapsed ? 'secondary' : 'body'}
                    >
                      {quantity}
                    </Text>
                  </td>
                  <td>
                    <PriceDisplay
                      amount={addOnCost ?? 0}
                      isCollapsed={isCollapsed}
                      period={period}
                    />
                  </td>
                  <td>
                    <PriceDisplay
                      amount={amount ?? 0}
                      highlightPrice={true}
                      isCollapsed={isCollapsed}
                      period={period}
                    />
                  </td>
                </tr>
              );
            }
          )}
        </tbody>
      </table>

      <div className={styles.contentContainer} data-collapsed={isCollapsed}>
        {/** subtotal */}
        {isNumber(subtotal) && (
          <div className={styles.wrapper}>
            <Text
              color="secondary"
              variant={isCollapsed ? 'secondary' : 'body'}
            >
              {TEXT.subtotal}
            </Text>
            <PriceDisplay
              amount={subtotal}
              isCollapsed={isCollapsed}
              period={period}
            />
          </div>
        )}

        {/** tax percent */}
        {taxLineItems !== undefined &&
          (noTaxCharged ? (
            <div className={styles.wrapper}>
              <div className={styles.taxPercentWrapper}>
                <Text
                  color="secondary"
                  variant={isCollapsed ? 'secondary' : 'body'}
                >
                  {getNoTaxChargedText(
                    isTaxExempt,
                    countryNameFromAbbreviation
                  )}
                </Text>
              </div>
              <PriceDisplay
                amount={0}
                isCollapsed={isCollapsed}
                period={period}
              />
            </div>
          ) : (
            taxLineItems?.map((taxLineItem) => {
              const amount = taxLineItem?.amount;
              const displayName = taxLineItem?.displayName;
              const rate = taxLineItem?.rate;

              if (isNumber(amount) && isNumber(rate)) {
                return (
                  <div
                    key={`${displayName}-${rate}`}
                    className={styles.wrapper}
                  >
                    <div className={styles.taxPercentWrapper}>
                      <Text
                        color="secondary"
                        variant={isCollapsed ? 'secondary' : 'body'}
                      >
                        {countryNameFromAbbreviation
                          ? TEXT.taxPercentVAT(
                              rate,
                              countryNameFromAbbreviation
                            )
                          : TEXT.taxPercentOther(rate, displayName ?? '')}
                      </Text>

                      {shouldShowMaybeHasErrorValidatingVat && (
                        <Tooltip
                          placement="top"
                          title={TEXT.toolTip}
                          zIndex={theme.zIndices.tooltip}
                        >
                          <IconInfo
                            color={theme.colors.icon.secondary}
                            size={16}
                          />
                        </Tooltip>
                      )}
                    </div>
                    <PriceDisplay
                      amount={tax}
                      isCollapsed={isCollapsed}
                      period={period}
                    />
                  </div>
                );
              }
              return null;
            })
          ))}

        {!isCollapsed && <Separator />}

        {/* doesnt show if not billed immedietly */}
        {isNumber(total) && isDueNow && (
          <div className={styles.wrapper}>
            <Text
              color="primary"
              fontWeight={isCollapsed ? 'regular' : 'semiBold'}
              variant={isCollapsed ? 'secondary' : 'body'}
            >
              {TEXT.total}
            </Text>
            <PriceDisplay
              amount={total}
              highlightPrice={true}
              isCollapsed={isCollapsed}
              period={period}
            />
          </div>
        )}

        {/* after free trial */}
        {showTrialTotal && isNumber(total) && (
          <div className={styles.wrapper}>
            <Text
              color="secondary"
              variant={isCollapsed ? 'secondary' : 'body'}
            >
              {promotionExpiresAt &&
                TEXT.afterFreeTrialEnds(new Date(promotionExpiresAt))}
            </Text>
            <PriceDisplay
              amount={total}
              highlightPrice={true}
              isCollapsed={isCollapsed}
              period={period}
            />
          </div>
        )}

        {/* due now */}
        {isNumber(dueNow) && showDueNow && (
          <div className={styles.wrapper}>
            <Text
              color="primary"
              fontWeight={isCollapsed ? 'regular' : 'semiBold'}
              variant={isCollapsed ? 'secondary' : 'body'}
            >
              {isDueNow && !isCurrentUserSubscriptionFree && !isTrialActive
                ? TEXT.dueNowWithAsterisk
                : TEXT.dueNowRegular}
            </Text>
            <Text
              fontWeight={isCollapsed ? 'regular' : 'semiBold'}
              variant={isCollapsed ? 'secondary' : 'body'}
            >
              {formatCents(dueNow, true)}
            </Text>
          </div>
        )}
      </div>
    </>
  );
};
